// Cookie consent for analytics (GDPR / ePrivacy). Visitors from the regions
// below see the consent banner; until they accept, PostHog runs cookieless.
// The country comes from the `mt_country` cookie the proxy copies from
// Vercel's `x-vercel-ip-country` header.

export const COUNTRY_COOKIE = 'mt_country'
const CHOICE_KEY = 'mt_consent'
const REGION_OVERRIDE_KEY = 'mt_consent_region'

// EU-27, the rest of the EEA (IS, LI, NO), the UK and Switzerland.
export const CONSENT_REGIONS: ReadonlySet<string> = new Set([
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
  'IS',
  'LI',
  'NO',
  'GB',
  'CH',
])

export type ConsentChoice = 'granted' | 'denied'

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
  return match ? decodeURIComponent(match[1]) : null
}

// Local testing only: `?consent_region=eu` / `?consent_region=us` pins the
// region for the tab. Never honored in production, where a shared link could
// otherwise switch an EU visitor's banner off.
function devRegionOverride(): 'eu' | 'us' | null {
  if (process.env.NODE_ENV === 'production') return null
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('consent_region')
    if (fromUrl === 'eu' || fromUrl === 'us') sessionStorage.setItem(REGION_OVERRIDE_KEY, fromUrl)
    const stored = sessionStorage.getItem(REGION_OVERRIDE_KEY)
    return stored === 'eu' || stored === 'us' ? stored : null
  } catch {
    return null
  }
}

/**
 * Whether this visitor must opt in before analytics may use cookies. A missing
 * country in production fails closed (consent required); in development it
 * defaults to not required.
 */
export function consentRequired(): boolean {
  if (typeof window === 'undefined') return false
  const override = devRegionOverride()
  if (override) return override === 'eu'
  const country = readCookie(COUNTRY_COOKIE)
  if (country) return CONSENT_REGIONS.has(country.toUpperCase())
  return process.env.NODE_ENV === 'production'
}

export function readConsentChoice(): ConsentChoice | null {
  try {
    const value = localStorage.getItem(CHOICE_KEY)
    return value === 'granted' || value === 'denied' ? value : null
  } catch {
    return null
  }
}

export function storeConsentChoice(choice: ConsentChoice): void {
  try {
    localStorage.setItem(CHOICE_KEY, choice)
  } catch {
    // Storage blocked: the choice still applies for this page view.
  }
}
